/**
 * 📅 Booking *Request* Status — Value Object
 *
 * 🔄 نموذج العمل الجديد (MVP — Lead Generation):
 *   حفلة منصة اكتشاف قاعات + توليد طلبات (leads). الطلب لا يحجز القاعة.
 *   الحجز الحقيقي (المعاينة، العربون، الاتفاق) يتم خارج التطبيق بين العميل والمزود.
 *   التاريخ لا يصبح "محجوزاً" إلا بإجراء صريح من المزود (راجع AvailabilityStatus).
 *
 * State Machine للطلب:
 *
 *   pending ──→ contacted
 *      │            │
 *      ├────────────┼──→ completed_externally (تم الحجز/الاتفاق خارج التطبيق — إجراء المزود)
 *      ├────────────┼──→ rejected             (المزود لم يقبل الطلب)
 *      ├────────────┼──→ cancelled             (العميل ألغى)
 *      ├────────────┼──→ booked_elsewhere      (طلب آخر فاز بالتاريخ — تلقائي)
 *      └────────────┴──→ expired               (مرّت المهلة أو تاريخ المناسبة — تلقائي)
 *
 * ⚠️ Invariants:
 *   - completed_externally / rejected / cancelled / booked_elsewhere / expired نهائية
 *   - completed_externally هو "تم الحجز فعلاً" على مستوى الطلب (lead)؛
 *     يبقى منفصلاً عن AvailabilityStatus.BOOKED الذي يمثّل توفّر التاريخ نفسه.
 */

export const BookingRequestStatus = {
  /** بانتظار مراجعة المزود — بعد إرسال الطلب مباشرة */
  PENDING: 'pending',
  /** تم التواصل — المزود تواصل مع العميل (لا يعني الحجز) */
  CONTACTED: 'contacted',
  /** تم الحجز — أُكّد الاتفاق/الحجز خارج التطبيق بإجراء صريح من المزود (نهائية) */
  COMPLETED_EXTERNALLY: 'completed_externally',
  /** لم يتم قبول الطلب — المزود رفض أو لم يتفق */
  REJECTED: 'rejected',
  /** ألغي من العميل */
  CANCELLED: 'cancelled',
  /** أُغلق لأن التاريخ حُجز — عميل آخر فاز بنفس التاريخ (تلقائي) */
  BOOKED_ELSEWHERE: 'booked_elsewhere',
  /** انتهت صلاحية الطلب — مرّت المهلة أو تاريخ المناسبة (تلقائي) */
  EXPIRED: 'expired',
} as const;

export type BookingRequestStatusType = (typeof BookingRequestStatus)[keyof typeof BookingRequestStatus];

/**
 * 🟢 حالات التوفّر (Availability) — منفصلة عن حالة الطلب.
 *
 * تُحفظ في venues/{venueId}/booking_dates وتمثّل "صدق التوفّر" الحقيقي:
 *   - available     : لا طلبات نشطة ولا حجز/حجب → السماح بإرسال طلب
 *   - has_requests  : يوجد طلب أو أكثر، لكن لا حجز مؤكّد → مؤشر طلب + السماح بالطلب
 *   - booked        : المزود أكّد أن التاريخ محجوز → تعطيل الطلب
 *   - blocked       : المزود حجب التاريخ (صيانة/مناسبة خاصة) → تعطيل الطلب
 */
export const AvailabilityStatus = {
  AVAILABLE: 'available',
  HAS_REQUESTS: 'has_requests',
  BOOKED: 'booked',
  BLOCKED: 'blocked',
} as const;

export type AvailabilityStatusType =
  (typeof AvailabilityStatus)[keyof typeof AvailabilityStatus];

/**
 * 🕐 فترات الـ slot — تدخل في doc id لـ booking_dates: `${eventDate}_${timeSlot}`.
 *    حالياً 'evening' فقط؛ 'morning' محجوزة للمستقبل (تطابق Flutter TimeSlot).
 */
export const TIME_SLOT = {
  EVENING: 'evening',
  MORNING: 'morning',
} as const;

export type TimeSlotType = (typeof TIME_SLOT)[keyof typeof TIME_SLOT];

/**
 * 🎨 ألوان وتسميات كل حالة طلب (للـ UI)
 */
export const BookingRequestStatusUI: Record<BookingRequestStatusType, {
  bg: string;
  text: string;
  border: string;
  label: string;
  emoji: string;
  description: string;
}> = {
  [BookingRequestStatus.PENDING]: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-300',
    label: 'بانتظار المراجعة',
    emoji: '⏳',
    description: 'ينتظر مراجعة المزود',
  },
  [BookingRequestStatus.CONTACTED]: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-300',
    label: 'تم التواصل',
    emoji: '📞',
    description: 'المزود تواصل مع العميل',
  },
  [BookingRequestStatus.COMPLETED_EXTERNALLY]: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-300',
    label: 'تم الحجز',
    emoji: '🎉',
    description: 'تم الاتفاق والحجز خارج التطبيق',
  },
  [BookingRequestStatus.REJECTED]: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-300',
    label: 'لم يتم قبول الطلب',
    emoji: '❌',
    description: 'المزود لم يقبل الطلب',
  },
  [BookingRequestStatus.CANCELLED]: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-300',
    label: 'ألغي من العميل',
    emoji: '🚫',
    description: 'العميل ألغى الطلب',
  },
  [BookingRequestStatus.BOOKED_ELSEWHERE]: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-300',
    label: 'خسر الحجز',
    emoji: '📅',
    description: 'حُجز التاريخ لعميل آخر',
  },
  [BookingRequestStatus.EXPIRED]: {
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    border: 'border-slate-300',
    label: 'انتهت الصلاحية',
    emoji: '⌛',
    description: 'انتهت مهلة الطلب',
  },
};

/**
 * 🛡️ Transition Rules
 *
 * ملاحظة: الانتقالات إلى booked_elsewhere و expired تُنفّذها Cloud Functions
 * (تلقائياً) من pending أو contacted.
 */
export const BookingTransitions: Record<BookingRequestStatusType, BookingRequestStatusType[]> = {
  [BookingRequestStatus.PENDING]: [
    BookingRequestStatus.CONTACTED,
    BookingRequestStatus.COMPLETED_EXTERNALLY,
    BookingRequestStatus.REJECTED,
    BookingRequestStatus.CANCELLED,
    BookingRequestStatus.BOOKED_ELSEWHERE,
    BookingRequestStatus.EXPIRED,
  ],
  [BookingRequestStatus.CONTACTED]: [
    BookingRequestStatus.COMPLETED_EXTERNALLY,
    BookingRequestStatus.REJECTED,
    BookingRequestStatus.CANCELLED,
    BookingRequestStatus.BOOKED_ELSEWHERE,
    BookingRequestStatus.EXPIRED,
  ],
  [BookingRequestStatus.COMPLETED_EXTERNALLY]: [], // نهائي
  [BookingRequestStatus.REJECTED]: [], // نهائي
  [BookingRequestStatus.CANCELLED]: [], // نهائي
  [BookingRequestStatus.BOOKED_ELSEWHERE]: [], // نهائي
  [BookingRequestStatus.EXPIRED]: [], // نهائي
};

export function canTransitionBooking(
  from: BookingRequestStatusType,
  to: BookingRequestStatusType
): boolean {
  return BookingTransitions[from].includes(to);
}

/**
 * الحالات النشطة — طلب لم يُحسم بعد (يُحتسب في مؤشر الطلب activeRequestCount).
 */
export const ACTIVE_REQUEST_STATUSES: BookingRequestStatusType[] = [
  BookingRequestStatus.PENDING,
  BookingRequestStatus.CONTACTED,
];

/**
 * هل الطلب نشط؟ (لم يُرفض/يُلغَ/تنتهِ صلاحيته/يُغلق)
 *
 * ⚠️ "نشط" هنا = lead حيّ. هذا لا يعني أن التاريخ محجوز —
 *    التوفّر حالة منفصلة (AvailabilityStatus).
 */
export function isActiveBooking(status: BookingRequestStatusType): boolean {
  return status === BookingRequestStatus.PENDING || status === BookingRequestStatus.CONTACTED;
}

/**
 * هل الطلب يحتاج action من المزود؟ (لم يتواصل بعد)
 */
export function needsVendorAction(status: BookingRequestStatusType): boolean {
  return status === BookingRequestStatus.PENDING;
}
